// framework/client/core/PermissionsAPI.js
import { ComponentBase } from "./ComponentBase.js";

export class PermissionsAPI extends ComponentBase {
    constructor(config) {
        super(config);
        this.appClient = config.appClient;
    }

    async isPermissionGiven(permissionId) {
        if (!this.appClient.platform) return false;
        const res = await this.appClient.platform.checkPermission(permissionId);
        return res.status === 'granted';
    }

    async isPermissionDenied(permissionId) {
        if (!this.appClient.platform) return true;
        const res = await this.appClient.platform.checkPermission(permissionId);
        if (res.status === 'denied') return true;

        // Check if recently declined and cached in localStorage
        const declinedAtStr = localStorage.getItem('appbuilder:perm:declined:' + permissionId);
        if (declinedAtStr) {
            const declinedAt = parseInt(declinedAtStr, 10);
            const oneWeek = 7 * 24 * 60 * 60 * 1000;
            // Suppress modal prompt for 7 days after they decline
            if (Date.now() - declinedAt < oneWeek) {
                return true;
            }
        }
        return false;
    }

    async getPermission(permissionId, options = {}) {
        const isGiven = await this.isPermissionGiven(permissionId);
        if (isGiven) {
            return { status: 'granted' };
        }

        const isDenied = await this.isPermissionDenied(permissionId);
        if (isDenied && !options.force) {
            return { status: 'denied' };
        }

        // Dynamically import PermissionManager to show the modal to prevent circular dependencies
        const { PermissionManager } = await import("../components/PermissionManager.js");
        const results = await PermissionManager.showModal(this.appClient, [permissionId], options);
        return { status: results[permissionId] || 'denied' };
    }

    async promptFirstTimePermissions(force = false) {
        if (this._isPrompting) return;

        if (!force && localStorage.getItem('appbuilder:first-time-permissions-shown')) {
            return;
        }

        this._isPrompting = true;
        try {
            let clientConfig = this.appClient.sessionTokenHandler?.getClientConfig();
            if (clientConfig instanceof Promise) {
                clientConfig = await clientConfig;
            }
            if (!clientConfig) {
                clientConfig = this.appClient.getSharedData('config') || {};
            }

            const declared = clientConfig.permissions || {};
            const permissionIds = Object.keys(declared);

            if (permissionIds.length === 0) {
                return;
            }

            this.log.info("Prompting user for declared permissions:", permissionIds);
            const { PermissionManager } = await import("../components/PermissionManager.js");
            await PermissionManager.showModal(this.appClient, permissionIds, { force: true });

            localStorage.setItem('appbuilder:first-time-permissions-shown', 'true');
            this.log.info("First-time permissions onboarding completed successfully.");
        } catch (err) {
            this.log.warn("Permissions prompt note:", err);
        } finally {
            this._isPrompting = false;
        }
    }
}
